import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { useIsMobile } from '@noor/lib';
import type { ModalRenderProps } from '@noor/lib';
import { useZodForm } from '@noor/ui';
import { useMenuProduct, type MenuProduct } from '@/entities/menu';
import { buildProductFormSchema, type ProductFormValues } from '../model/schema';
import { buildDefaults } from '../lib/product-form';
import { ProductModalDesktop } from './product-modal.desktop';
import { ProductModalMobile } from './product-modal.mobile';

type ProductModalProps =
  | ModalRenderProps<'add-product'>
  | ModalRenderProps<'edit-product'>;

/**
 * Add/edit product, registered under `add-product`/`edit-product`.
 *
 * Picks between two distinct trees, mirroring the page-level `.mobile.tsx`
 * split (see CLAUDE.md §6c): mobile is a full-screen native-app takeover
 * (Figma 26609:10817 → 26612:18208), not a narrow rendering of the desktop
 * dialog wizard. Both branches share the same schema, defaults/mappers and
 * mutation hooks — only the view differs.
 */
export function ProductModal(props: ProductModalProps) {
  const { t } = useTranslation('eats');
  const product = props.payload.mode === 'edit' ? props.payload.product : undefined;
  const { defaultCategoryId } = props.payload;
  const { data: fetched } = useMenuProduct(product?.id);
  const source = useMemo<MenuProduct | undefined>(
    () => (product ? { ...product, ...fetched } : undefined),
    [product, fetched],
  );
  // Own the form above the responsive branch so a breakpoint change cannot
  // discard values entered in the other view.
  const form = useZodForm(buildProductFormSchema(t, props.payload.mode === 'edit'), {
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: buildDefaults(source, defaultCategoryId),
  }) as unknown as UseFormReturn<ProductFormValues>;
  return useIsMobile() ? (
    <ProductModalMobile {...props} form={form} />
  ) : (
    <ProductModalDesktop {...props} form={form} />
  );
}
